import { useEffect, useState } from "react";
import { Save, Building2, Landmark, FileText, ClipboardList, BadgeCheck, Mail, PlugZap, Unplug, MessageCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";

const Field = ({ label, k, form, set, mono, textarea, testid }) => (
  <div>
    <label className="sgp-label">{label}</label>
    {textarea ? (
      <textarea data-testid={testid} rows={3} className="sgp-input h-auto py-2" value={form[k] || ""} onChange={(e) => set(k, e.target.value)} />
    ) : (
      <input data-testid={testid} className={`sgp-input ${mono ? "font-mono" : ""}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} />
    )}
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div className="sgp-card p-5">
    <h3 className="font-heading font-bold text-slate-800 flex items-center gap-2 mb-4">
      <Icon className="h-4 w-4 text-teal-700" /> {title}
    </h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
  </div>
);

export default function Settings() {
  const { workspace } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mailStatus, setMailStatus] = useState(null);
  const [mailSaving, setMailSaving] = useState(false);
  const [mailForm, setMailForm] = useState({ provider: "gmail", email: "", password: "" });
  const [whatsAppStatus, setWhatsAppStatus] = useState(null);
  const [whatsAppSaving, setWhatsAppSaving] = useState(false);
  const [whatsAppForm, setWhatsAppForm] = useState({ phone_number_id: "", access_token: "" });
  const { t } = useLang();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => {});
    api.get("/mail/status").then((r) => {
      setMailStatus(r.data);
      if (r.data?.from) setMailForm((m) => ({ ...m, email: r.data.from, provider: r.data.provider || m.provider }));
    }).catch(() => setMailStatus({ configured: false, outlook_connected: false }));
    api.get("/whatsapp/status").then((r) => {
      setWhatsAppStatus(r.data);
      if (r.data?.phone_number_id) setWhatsAppForm((w) => ({ ...w, phone_number_id: r.data.phone_number_id }));
    }).catch(() => setWhatsAppStatus({ configured: false }));

    const params = new URLSearchParams(window.location.search);
    const outlookResult = params.get("outlook");
    if (outlookResult === "connected") toast.success("Outlook connected — logged-in Microsoft account ab direct sender hai");
    else if (outlookResult && outlookResult !== "connected") {
      const errors = {
        cancelled: "Microsoft connection was cancelled. Click Connect Outlook to try again.",
        expired: "The Microsoft connection link expired. Click Connect Outlook again.",
        email_mismatch: "Choose the Microsoft work account with the same email as your SGP login.",
        user_missing: "Your SGP account is unavailable. Ask your administrator to check it.",
        no_refresh_token: "Microsoft did not grant mailbox access. Reconnect and accept the requested permissions.",
        token_error: "Microsoft could not complete mailbox authorization. Reconnect, or ask your Microsoft 365 administrator to check consent.",
        profile_error: "Microsoft could not verify your mailbox. Reconnect and accept the requested permissions.",
      };
      toast.error(errors[outlookResult] || "Outlook connection failed. Please reconnect.");
    }
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Settings saved — sab documents pe apply ho jayega");
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const connectMail = async () => {
    if (!mailForm.email || !mailForm.password) return toast.error("Sender email aur app/SMTP password chahiye");
    setMailSaving(true);
    try {
      const { data } = await api.post("/mail/config", mailForm);
      const { data: status } = await api.get("/mail/status");
      setMailStatus(status);
      setMailForm((m) => ({ ...m, password: "" }));
      toast.success(`${data.provider === "gmail" ? "Gmail" : "Outlook"} connected — PDFs ab direct email ho sakte hain`);
    } catch (err) { toast.error(err.message); }
    setMailSaving(false);
  };

  const disconnectMail = async () => {
    setMailSaving(true);
    try {
      await api.delete("/mail/config");
      const { data } = await api.get("/mail/status");
      setMailStatus(data);
      setMailForm((m) => ({ ...m, password: "" }));
      toast.success("Company Gmail disconnected");
    } catch (err) { toast.error(err.message); }
    setMailSaving(false);
  };

  const connectOutlook = async () => {
    try {
      const { data } = await api.post("/mail/outlook/connect");
      window.location.href = data.authorize_url;
    } catch (err) { toast.error(err.message); }
  };

  const disconnectOutlook = async () => {
    setMailSaving(true);
    try {
      await api.delete("/mail/outlook");
      const { data } = await api.get("/mail/status");
      setMailStatus(data);
      toast.success("Outlook disconnected");
    } catch (err) { toast.error(err.message); }
    setMailSaving(false);
  };

  const connectWhatsApp = async () => {
    if (!whatsAppForm.phone_number_id || !whatsAppForm.access_token) return toast.error("Phone Number ID aur permanent access token chahiye");
    setWhatsAppSaving(true);
    try {
      const { data } = await api.post("/whatsapp/config", whatsAppForm);
      setWhatsAppStatus(data);
      setWhatsAppForm((w) => ({ ...w, access_token: "" }));
      toast.success("WhatsApp Business connected — PDFs direct chat me bhej sakte ho");
    } catch (err) { toast.error(err.message); }
    setWhatsAppSaving(false);
  };

  const disconnectWhatsApp = async () => {
    setWhatsAppSaving(true);
    try {
      await api.delete("/whatsapp/config");
      setWhatsAppStatus({ configured: false });
      setWhatsAppForm((w) => ({ ...w, access_token: "" }));
      toast.success("WhatsApp Business disconnected");
    } catch (err) { toast.error(err.message); }
    setWhatsAppSaving(false);
  };

  if (!form) return <p className="text-sm text-slate-400 py-10 text-center">Loading settings...</p>;

  return (
    <div data-testid="settings-page" className="sgp-page max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="sgp-page-title">{t("settings")}</h1>
          <p className="sgp-page-subtitle">{workspace?.company?.name || "Company"} ki legal/business details — is company ke invoice, quotation aur letterhead par yahi dikhega.</p>
        </div>
        <button data-testid="settings-save-btn" onClick={save} disabled={saving}
          className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-md bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-50">
          <Save className="h-4 w-4" /> {saving ? "Saving..." : t("save")}
        </button>
      </div>

      <Section icon={Building2} title="Business Identity">
        <Field label="Firm Name (Bills pe bada heading)" k="name" form={form} set={set} testid="set-name" />
        <Field label="Trade Name" k="trade_name" form={form} set={set} testid="set-trade-name" />
        <div className="sm:col-span-2"><Field label="Tagline (letterhead pe)" k="tagline" form={form} set={set} testid="set-tagline" /></div>
        <div className="sm:col-span-2"><Field label="Address" k="address" form={form} set={set} testid="set-address" /></div>
        <Field label="Phone / Mobile" k="phone" form={form} set={set} testid="set-phone" />
        <Field label="Email" k="email" form={form} set={set} testid="set-email" />
        <Field label="Certification (e.g. ISO 9001:2015)" k="certification" form={form} set={set} testid="set-certification" />
        <Field label="Established / Since Year" k="established_year" form={form} set={set} mono testid="set-established-year" />
        <Field label="Quotation Prefix (e.g. SGP)" k="quote_prefix" form={form} set={set} mono testid="set-quote-prefix" />
      </Section>

      <Section icon={BadgeCheck} title="Tax & Licenses">
        <div><label className="sgp-label">Minimum HSN digits (annual turnover)</label><select data-testid="set-hsn-min-digits" className="sgp-input" value={form.hsn_min_digits || 6} onChange={e => set("hsn_min_digits", Number(e.target.value))}><option value={6}>6 — above ₹5 crore</option><option value={4}>4 — up to ₹5 crore</option></select></div>
        <Field label="GSTIN" k="gstin" form={form} set={set} mono testid="set-gstin" />
        <Field label="Drug License No. (20B/21B)" k="dl_no" form={form} set={set} mono testid="set-dl" />
        <Field label="PAN" k="pan" form={form} set={set} mono testid="set-pan" />
        <Field label="State Code (GST)" k="state_code" form={form} set={set} mono testid="set-state-code" />
        <Field label="PIN Code" k="pincode" form={form} set={set} mono testid="set-pincode" />
        <div>
          <label className="sgp-label">Annual Aggregate Turnover (₹ Crore)</label>
          <input data-testid="set-aato" type="number" min="0" step="0.01" className="sgp-input font-mono"
            value={form.annual_turnover_crore ?? 0} onChange={(e) => set("annual_turnover_crore", Number(e.target.value) || 0)} />
          <p className="text-[10px] text-slate-500 mt-1">Used for e-Invoice compliance warnings; keep this updated from audited/GST turnover.</p>
        </div>
        <Field label="Jurisdiction (disputes ke liye)" k="jurisdiction" form={form} set={set} testid="set-jurisdiction" />
      </Section>

      <Section icon={Landmark} title="Bank Details (invoice pe payment ke liye)">
        <div className="sm:col-span-2"><Field label="Bank Name & Branch" k="bank_name" form={form} set={set} testid="set-bank-name" /></div>
        <Field label="Account No." k="bank_account_no" form={form} set={set} mono testid="set-bank-ac" />
        <Field label="IFSC" k="ifsc" form={form} set={set} mono testid="set-ifsc" />
        <Field label="UPI ID" k="upi" form={form} set={set} mono testid="set-upi" />
      </Section>

      <div className="sgp-card p-5">
        <div className="mb-4">
          <h3 className="font-heading font-bold text-slate-800 flex items-center gap-2">
            <Mail className="h-4 w-4 text-teal-700" /> Document Email
          </h3>
          <p className="text-xs text-slate-500 mt-1">PDF screen par 3 sending choices milengi: Direct Send, Gmail, aur logged-in user's Outlook.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-bold text-slate-800">Company Gmail</p>
                <p className="text-[11px] text-slate-500">Shared company sender / Direct Send fallback</p>
              </div>
              <span className={`text-[10px] font-bold rounded-full px-2 py-1 border ${mailStatus?.configured && mailStatus?.provider === "gmail" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                {mailStatus?.configured && mailStatus?.provider === "gmail" ? "Connected" : "Not connected"}
              </span>
            </div>

            {mailStatus?.configured && mailStatus?.provider === "gmail" ? (
              <div className="mt-3 flex items-center justify-between gap-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2">
                <p className="text-xs font-semibold text-emerald-800">{mailStatus.from}</p>
                <button type="button" onClick={disconnectMail} disabled={mailSaving} className="text-xs font-semibold text-rose-700">
                  <Unplug className="h-3.5 w-3.5 inline mr-1" /> Disconnect
                </button>
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                <input type="email" className="sgp-input" placeholder="company@gmail.com" value={mailForm.email}
                  onChange={(e) => setMailForm({ ...mailForm, provider: "gmail", email: e.target.value })} />
                <input type="password" className="sgp-input" autoComplete="new-password" placeholder="Google app password" value={mailForm.password}
                  onChange={(e) => setMailForm({ ...mailForm, provider: "gmail", password: e.target.value })} />
                <button type="button" onClick={connectMail} disabled={mailSaving}
                  className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                  <PlugZap className="h-3.5 w-3.5" /> {mailSaving ? "Connecting..." : "Connect Gmail"}
                </button>
                <p className="text-[10px] text-slate-500">Google 2-Step Verification ke baad App Password use karein; normal Gmail password nahi.</p>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-bold text-slate-800">Outlook / Microsoft 365</p>
                <p className="text-[11px] text-slate-500">Logged-in SGP user ka own Microsoft mailbox</p>
              </div>
              <span className={`text-[10px] font-bold rounded-full px-2 py-1 border ${mailStatus?.outlook_connected ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                {mailStatus?.outlook_connected ? "Connected" : "Not connected"}
              </span>
            </div>

            {mailStatus?.outlook_connected ? (
              <div className="mt-3 flex items-center justify-between gap-3 rounded-md bg-blue-50 border border-blue-200 px-3 py-2">
                <div>
                  <p className="text-xs font-semibold text-blue-900">{mailStatus.outlook_from}</p>
                  <p className="text-[10px] text-blue-700">Microsoft Graph · Mail.Send</p>
                </div>
                <button type="button" onClick={disconnectOutlook} disabled={mailSaving} className="text-xs font-semibold text-rose-700">
                  <Unplug className="h-3.5 w-3.5 inline mr-1" /> Disconnect
                </button>
              </div>
            ) : (
              <div className="mt-3">
                <button type="button" onClick={connectOutlook}
                  className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md bg-blue-700 text-white hover:bg-blue-800">
                  <ExternalLink className="h-3.5 w-3.5" /> Connect Outlook / Microsoft 365
                </button>
                <p className="text-[10px] text-slate-500 mt-2">
                  Microsoft login/consent ke baad sender wahi mailbox hoga jo current SGP user ke email se match karta hai.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="sgp-card p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading font-bold text-slate-800 flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-emerald-600" /> WhatsApp Business — Direct PDF
            </h3>
            <p className="text-xs text-slate-500 mt-1">No download / no Windows share panel: Meta WhatsApp Business Cloud API se specific chat ko PDF direct bhejne ke liye.</p>
          </div>
          <span className={`text-[10px] font-bold rounded-full px-2 py-1 border ${whatsAppStatus?.configured ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
            {whatsAppStatus?.configured ? "Connected" : "Not connected"}
          </span>
        </div>

        {whatsAppStatus?.configured ? (
          <div className="mt-4 flex items-center justify-between gap-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2">
            <div>
              <p className="text-xs font-semibold text-emerald-900">{whatsAppStatus.display_phone || "WhatsApp Business connected"}</p>
              <p className="text-[10px] text-emerald-700">Phone Number ID: {whatsAppStatus.phone_number_id}</p>
            </div>
            <button type="button" onClick={disconnectWhatsApp} disabled={whatsAppSaving} className="text-xs font-semibold text-rose-700">
              <Unplug className="h-3.5 w-3.5 inline mr-1" /> Disconnect
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <div><label className="sgp-label">WhatsApp Phone Number ID</label>
              <input className="sgp-input font-mono" value={whatsAppForm.phone_number_id}
                onChange={(e) => setWhatsAppForm({ ...whatsAppForm, phone_number_id: e.target.value })} /></div>
            <div><label className="sgp-label">Permanent Access Token</label>
              <input type="password" className="sgp-input" autoComplete="new-password" value={whatsAppForm.access_token}
                onChange={(e) => setWhatsAppForm({ ...whatsAppForm, access_token: e.target.value })} /></div>
            <div className="sm:col-span-2 flex items-center justify-between gap-3">
              <p className="text-[10px] text-slate-500">Token encrypted store hota hai aur browser ko wapas nahi diya jata.</p>
              <button type="button" onClick={connectWhatsApp} disabled={whatsAppSaving}
                className="shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                <PlugZap className="h-3.5 w-3.5" /> {whatsAppSaving ? "Connecting..." : "Connect & Test"}
              </button>
            </div>
          </div>
        )}
      </div>
      <Section icon={FileText} title="Invoice Terms & Conditions">
        <div className="sm:col-span-2"><Field label="Har Sales Bill ke footer mein yeh terms print hongi" k="invoice_terms" form={form} set={set} textarea testid="set-invoice-terms" /></div>
      </Section>

      <Section icon={ClipboardList} title="Quotation Notes">
        <div className="sm:col-span-2"><Field label="Har Quotation letter mein yeh notes print honge" k="quotation_notes" form={form} set={set} textarea testid="set-quotation-notes" /></div>
      </Section>
    </div>
  );
}

